import { useEffect, useState } from "react";

type Status =
  | { status: "unconfigured" }
  | {
      status: "verified";
      accountId: string;
      livemode: boolean;
      chargesEnabled: boolean;
      payoutsEnabled: boolean;
      detailsSubmitted: boolean;
      checkedAt: string;
    };
export function decodeStripeConnection(value: unknown): Status {
  if (!value || typeof value !== "object")
    throw new Error("Stripe connection details could not be read.");
  const data = value as Record<string, unknown>;
  if (
    data.provider !== "stripe" ||
    data.readOnly !== true ||
    data.billingEnabled !== false
  )
    throw new Error("Stripe connection details could not be read.");
  if (data.status === "unconfigured") return { status: "unconfigured" };
  if (
    data.status !== "verified" ||
    typeof data.accountId !== "string" ||
    !/^acct_[A-Za-z0-9]{1,200}$/.test(data.accountId) ||
    typeof data.checkedAt !== "string" ||
    !Number.isFinite(Date.parse(data.checkedAt)) ||
    !["livemode", "chargesEnabled", "payoutsEnabled", "detailsSubmitted"].every(
      (key) => typeof data[key] === "boolean",
    )
  )
    throw new Error("Stripe connection details could not be read.");
  return {
    status: "verified",
    accountId: data.accountId,
    checkedAt: data.checkedAt,
    livemode: data.livemode as boolean,
    chargesEnabled: data.chargesEnabled as boolean,
    payoutsEnabled: data.payoutsEnabled as boolean,
    detailsSubmitted: data.detailsSubmitted as boolean,
  };
}
export function StripeConnectionPanel({
  api,
}: {
  api: <T>(path: string, options?: { signal?: AbortSignal }) => Promise<T>;
}) {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setStatus(null);
    api<unknown>("/admin/billing/stripe/status", { signal: controller.signal })
      .then((value) => {
        if (!controller.signal.aborted)
          setStatus(decodeStripeConnection(value));
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError((cause as Error).message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [api, refresh]);
  return (
    <section className="cr-panel" aria-labelledby="stripe-connection-title">
      <h2 id="stripe-connection-title">Stripe billing</h2>
      <p>
        Stripe is the selected payment provider. Checking this connection does
        not start billing or charge a customer.
      </p>
      {loading && <p role="status">Checking Stripe connection…</p>}
      {error && (
        <p role="alert" className="cr-notice error">
          {error}
        </p>
      )}
      {status?.status === "unconfigured" && (
        <p>
          Not connected. Add the intended Stripe merchant account and its
          server-side credential before collecting payment details. Do not put
          API keys or card details in Messages.
        </p>
      )}
      {status?.status === "verified" && (
        <>
          <p>
            Account {status.accountId} verified in{" "}
            {status.livemode ? "live" : "test"} mode. Checked{" "}
            {new Date(status.checkedAt).toLocaleString()}.
          </p>
          <p>
            {status.chargesEnabled
              ? "Stripe allows this account to accept payments."
              : "Stripe has not enabled payments for this account."}{" "}
            {status.detailsSubmitted
              ? "Account details have been submitted."
              : "Stripe still needs account details."}
          </p>
          <p>
            Subscriptions and automatic charges are not enabled in this portal.
            Paid plans need approved prices and tax settings. Complimentary
            accounts do not require a payment method.
          </p>
        </>
      )}
      <button
        className="cr-button secondary"
        disabled={loading}
        onClick={() => setRefresh((value) => value + 1)}
      >
        Check connection
      </button>
    </section>
  );
}
